import React, { useContext, useEffect, useState } from 'react'
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  FlatList,
  ListRenderItem,
} from 'react-native'
import {
  BG_STYLES,
  BORDER_STYLES,
  COLORS,
  COMMON_STYLES,
  TEXT_STYLES,
  TYPOGRAPHY,
  VARIANTS_STYLES,
} from '@dehaat/dds'

import RoundImageWithName from 'app/components/reusable/RoundImageWithName'
import Loader from '../crops/Loader'
import { ResponsiveContext } from 'app/context/ResponsiveUIContext'
import { useNavigation } from '@react-navigation/native'
import { FarmerLivestock, Livestock } from '../../models/farmer'
import fetchFarmerLivestocks from '../../apis/fetchFarmerLivestocks'
import LivestocksSelectionModal from './LivestockSelectModal'
import { trackEvent } from '../../utils/tracking'
import { Farmer360UserInformationAdded } from '../../utils/event'

interface Props {
  name: string
  phoneNumber: string
  farmerId: string
  reload: () => void
  isEditAllowed: boolean
}

interface AddCropBtnProps {
  onClick: () => void
}

const AddCropButton = ({ onClick }: AddCropBtnProps) => {
  return (
    <Pressable onPress={onClick}>
      <View style={[styles.addCropBtn, styles.img100X100]}>
        <Text
          style={[
            TYPOGRAPHY.HEADING1,
            VARIANTS_STYLES.EMPHASIZED,
            TEXT_STYLES.textPrimary100,
          ]}
        >
          +
        </Text>
      </View>
      <Text
        style={[
          TYPOGRAPHY.HEADING3,
          VARIANTS_STYLES.MEDIUM,
          styles.textColor,
          COMMON_STYLES['mt1'],
        ]}
      >
        Add Animal
      </Text>
    </Pressable>
  )
}

const Livestocks = ({
  farmerId,
  reload,
  isEditAllowed,
  name,
  phoneNumber,
}: Props) => {
  const isFocused = useNavigation()
  const [isLoading, setIsLoading] = useState(true)
  const [livestocks, setLivestocks] = useState<FarmerLivestock[]>([])
  const [isAddLivestockModalVisible, setAddLivestockModalVisibility] =
    useState(false)

  const { mediaStyles } = useContext(ResponsiveContext)

  const fetchAndSetFarmerLivestocks = async () => {
    setIsLoading(true)
    const results = await fetchFarmerLivestocks(farmerId)
    if (results) {
      setLivestocks(results)
    }
    setIsLoading(false)
  }

  useEffect(() => {
    if (isFocused) fetchAndSetFarmerLivestocks()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFocused])

  const renderItem: ListRenderItem<Livestock> = ({ item }) => {
    const { id, image_url, name, quantity } = item

    return (
      <RoundImageWithName
        name={name}
        imgUrl={image_url}
        handleItemClick={() => {}}
        key={id}
        imageStyle={[
          BORDER_STYLES.borderNeutral10,
          COMMON_STYLES.b1,
          styles.img100X100,
        ]}
        textStyle={[
          TYPOGRAPHY.HEADING3,
          VARIANTS_STYLES.MEDIUM,
          styles.textColor,
        ]}
        textNumberOfLines={2}
        style={mediaStyles({
          desktopStyle: styles['mr5/2'],
          mobileStyle: COMMON_STYLES.mr1,
        })}
      >
        <Text
          style={[
            TYPOGRAPHY.CAPTION1,
            VARIANTS_STYLES.MEDIUM,
            TEXT_STYLES.textWhite,
            BG_STYLES.bgBlack,
            COMMON_STYLES['ph1/2'],
            COMMON_STYLES['pv1/4'],
            COMMON_STYLES['br1/4'],
            COMMON_STYLES['-mt1'],
          ]}
        >
          {quantity < 10 ? `0${quantity}` : quantity}
        </Text>
      </RoundImageWithName>
    )
  }

  return (
    <View
      style={[
        COMMON_STYLES.mv1,
        mediaStyles({ mobileStyle: COMMON_STYLES.ph1 }),
      ]}
    >
      <Text
        style={[
          TYPOGRAPHY.HEADING1,
          VARIANTS_STYLES.MEDIUM,
          TEXT_STYLES.textNeutral100,
        ]}
      >
        Livestock
      </Text>
      {isLoading ? (
        <Loader />
      ) : (
        <FlatList
          horizontal
          data={livestocks}
          renderItem={renderItem}
          keyExtractor={(item, index) => item.id}
          contentContainerStyle={[
            COMMON_STYLES.row,
            COMMON_STYLES.mv1,
            mediaStyles({ desktopStyle: COMMON_STYLES.flex1 }),
          ]}
          showsHorizontalScrollIndicator={false}
          ListFooterComponent={
            <>
              {isEditAllowed && (
                <AddCropButton
                  onClick={() => setAddLivestockModalVisibility(true)}
                />
              )}
            </>
          }
        />
      )}
      {isAddLivestockModalVisible && (
        <LivestocksSelectionModal
          farmerId={farmerId}
          livestocks={livestocks}
          onClose={() => setAddLivestockModalVisibility(false)}
          onSubmit={(livestocks: string) => {
            trackEvent(Farmer360UserInformationAdded, {
              farmername: name,
              farmermobile: phoneNumber,
              InformationType: 'livestocks',
              InformationUpdated: livestocks,
            })
            reload()
          }}
        />
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  img100X100: {
    height: 100,
    width: 100,
    borderRadius: 50,
  },
  addCropBtn: {
    borderWidth: 1,
    borderColor: COLORS.primary100,
    backgroundColor: COLORS.primary20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  'mr5/2': {
    marginRight: 40,
  },
  textColor: {
    color: '#4D4D4D',
  },
})

export default Livestocks
